import { isAfter, isBefore, subDays } from "date-fns";

import { formatDate, parseDate } from "@luce/core/utils";

import { InvoicePaymentStatusEnum, VoucherDiscountTypeEnum } from "../__generated__/graphql";
import { PAYNOW_PROCESSING_DAYS } from "./constant";
import type { InvoiceDetailData, InvoiceItem, InvoiceLineGroup } from "./types";

type InvoiceStatusTag = {
  label: "Paid" | "Processing" | "Underpaid" | "Overdue" | "Due";
  variant: "success" | "info" | "warning" | "danger";
};

const UNPAID_STATUSES = [InvoicePaymentStatusEnum.Unpaid, InvoicePaymentStatusEnum.Underpaid];

function toDate(value?: string | null) {
  return value ? parseDate(value) : null;
}

export function getInvoiceBalance(invoice: InvoiceItem) {
  return invoice.totalAmount - invoice.paidAmount;
}

export function getInvoiceStatusTag(invoice: InvoiceItem, now = new Date()): InvoiceStatusTag {
  const { paymentStatus, qrCodeUrl } = invoice;

  if (paymentStatus === InvoicePaymentStatusEnum.Paid) {
    return { label: "Paid", variant: "success" };
  }

  const isUnpaid = UNPAID_STATUSES.includes(paymentStatus);
  const dueDate = toDate(invoice.dueDate);
  const isOverdue = isUnpaid && dueDate !== null && isBefore(dueDate, subDays(now, 1));

  const indicatedAt = invoice.clientPaymentIndicated
    ? new Date(invoice.clientPaymentIndicated)
    : null;
  const isPayNowProcessing =
    Boolean(qrCodeUrl) &&
    indicatedAt !== null &&
    !Number.isNaN(indicatedAt.getTime()) &&
    isAfter(indicatedAt, subDays(now, PAYNOW_PROCESSING_DAYS)) &&
    !isOverdue;

  if (paymentStatus === InvoicePaymentStatusEnum.StripeProcessing || isPayNowProcessing) {
    return { label: "Processing", variant: "info" };
  }

  if (getInvoiceBalance(invoice) < invoice.totalAmount) {
    return { label: "Underpaid", variant: "warning" };
  }

  return { label: isOverdue ? "Overdue" : "Due", variant: "danger" };
}

export function getInvoiceTitle(invoice: InvoiceItem) {
  return invoice.label?.split(" - ")[0] ?? "-";
}

export function formatInvoiceDate(value?: string | null, pattern = "MMM dd, yyyy") {
  const date = toDate(value);
  return date ? formatDate(date, pattern) : "-";
}

export function getInvoiceDiscount({ appliedVoucher, lineItemsTotal }: InvoiceDetailData) {
  if (!appliedVoucher) return 0;
  return appliedVoucher.discountType === VoucherDiscountTypeEnum.Percentage
    ? (lineItemsTotal * appliedVoucher.discountValue) / 100
    : Math.min(appliedVoucher.discountValue, lineItemsTotal);
}

/** Same rule as the legacy reminder: PayNow invoice marked paid over 3 days ago, still unsettled. */
export function shouldShowPaymentProof(invoice: InvoiceDetailData, now = new Date()) {
  if (invoice.paymentStatus === InvoicePaymentStatusEnum.Paid || !invoice.qrCodeUrl) return false;
  if (!invoice.clientPaymentIndicated) return false;
  const indicatedAt = new Date(invoice.clientPaymentIndicated);
  return isBefore(indicatedAt, subDays(now, PAYNOW_PROCESSING_DAYS));
}

/** Pulls the signed `token` param from a PDF URL; the PDF routes resolve the file from it. */
export function getPdfToken(url?: string | null) {
  if (!url) return null;
  try {
    return new URL(url).searchParams.get("token");
  } catch {
    return null;
  }
}

/** Invoice descriptions are "<service> - <item>"; group items under their service, as today. */
export function groupInvoiceLineItems(
  lineItems: InvoiceDetailData["lineItems"],
): InvoiceLineGroup[] {
  const groups = new Map<string, InvoiceLineGroup>();

  (lineItems ?? []).forEach((lineItem, index) => {
    const splitAt = lineItem.description.lastIndexOf(" - ");
    const title = splitAt === -1 ? lineItem.description : lineItem.description.slice(0, splitAt);
    const label = splitAt === -1 ? lineItem.description : lineItem.description.slice(splitAt + 3);

    const group = groups.get(title) ?? { title, items: [] };
    group.items.push({
      key: `${lineItem.productCode}-${index}`,
      label: `#${index + 1} ${label}`,
      quantity: lineItem.quantity,
      amount: lineItem.amount,
    });
    groups.set(title, group);
  });

  return [...groups.values()];
}
